import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
} from 'react';
import { Space, Popover, Button, Tooltip, Input } from '@bedrock/components';
import { ChevronDown, Upload } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames/bind';
import { SUpload } from '../SUpload';
import TextAreaPopover, {
  TextAreaPopconfirmProps,
} from '#components/SForm/SFormElement/TextAreaPropover';
import styles from './index.less';

const cx = classNames.bind(styles);

type TextAreaWithUploadProps = {
  itemKey?: string | number;
  value?: {
    text: string;
    files: any[];
    itemKey: string | number;
  };
  onChange?: (value: {
    text: string;
    files: any[];
    itemKey: string | number;
  }) => void;
  view?: boolean;
  maxFileSize?: number;
  textAreaPopoverProps?: TextAreaPopconfirmProps;
  textAreaProps?: React.ComponentProps<typeof Input.TextArea>;
  uploadProps?: React.ComponentProps<typeof SUpload>;
  size?: 'small' | 'medium' | 'large';
};

const TextAreaWithUpload = forwardRef((props: TextAreaWithUploadProps, ref) => {
  const {
    itemKey,
    maxFileSize = 200 * 1024 * 1024,
    textAreaProps,
    uploadProps,
    size = 'medium',
  } = props;
  const {
    value = { text: '', files: [], itemKey },
    onChange,
    view = false,
  } = props;
  const uploadRef = useRef<any>(null);

  const [localFileList, setLocalFileList] = useState<any[]>(value?.files || []);

  const isUploading = useMemo(() => {
    return localFileList.some((n) => n.status === 'uploading');
  }, [localFileList]);

  const validateFields = useMemoizedFn(() => {
    return new Promise((resolve, reject) => {
      if (uploadRef.current) {
        uploadRef.current
          ?.validateFields?.()
          .then(() => {
            if (value.text || value.files?.length) {
              resolve(true);
            } else {
              reject('请输入结案报告或上传文件');
            }
          })
          .catch(reject);
      } else {
        resolve(true);
      }
    });
  });

  useImperativeHandle(ref, () => {
    return {
      validateFields,
    };
  });

  return (
    <div className={cx('closing-report')}>
      <div className={cx('text-area-container')}>
        <TextAreaPopover
          view={view}
          value={value.text}
          linkDetector
          onChange={(e: string) => {
            onChange?.({ ...value, itemKey, text: e?.trim?.() || '' } as any);
          }}
          {...props.textAreaPopoverProps}
          textAreaProps={textAreaProps}
        />
      </div>
      <Space className={cx('upload-container')} size={[8, 0]} align="start">
        {!view && (
          <SUpload
            className={cx('upload-button')}
            ref={uploadRef}
            trigger={
              <Tooltip
                title={`上传附件：单个文件不超过 ${(maxFileSize / 1024 / 1024).toFixed(0)}M`}
                placement="topRight"
              >
                <Button
                  type="checked-neutral"
                  className={cx('upload-trigger')}
                  icon={<Upload />}
                  loading={isUploading}
                  size={props?.textAreaPopoverProps?.defaultChildrenProps?.size}
                />
              </Tooltip>
            }
            tip={null}
            {...uploadProps}
            fileList={value.files as any}
            onChange={(file) => {
              onChange?.({ ...value, itemKey, files: file.dataList } as any);
              setLocalFileList(file.fileList);
              uploadProps?.onChange?.(file);
            }}
          />
        )}
        {!!value?.files?.length && (
          <Popover
            content={
              <div className={cx('upload-popover-content')}>
                <SUpload
                  className={cx('upload-list')}
                  fileSizeLimit={maxFileSize}
                  multiple
                  view={view}
                  tip={null}
                  {...uploadProps}
                  fileList={value.files as any}
                  onChange={(file) => {
                    onChange?.({ ...value, files: file.dataList } as any);
                    setLocalFileList(file.fileList);
                    uploadProps?.onChange?.(file);
                  }}
                />
              </div>
            }
            trigger="click"
            disabled={!value?.files?.length}
          >
            <Button
              type="link"
              className={cx('upload-trigger')}
              suffixIcon={<ChevronDown />}
              size={size}
            >
              {view
                ? `${value?.files?.length || 0}个附件`
                : `共${value?.files?.length || 0}个`}
            </Button>
          </Popover>
        )}
        {!value?.files?.length && !!view && (
          <span className={cx('file-empty-tip')}>无附件</span>
        )}
      </Space>
    </div>
  );
});

export default TextAreaWithUpload;
